import type { CatalogShow } from "@/types/catalogLayout.type";
import SeriesCover from "@/components/SeriesCover";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * A recommended series (SHO-757): the catalog's small cover with its title
 * under, as on a cast member's page. It opens the series page in both A/B
 * variants without exposing (`detailsPage="always"`): the visitor is already
 * on a series page, past the catalog decision the test measures.
 */
const RecommendedShowCard: React.FC<{
  show: CatalogShow;
  /** The `seriesDetailsOrigin` the opened page reports. */
  origin: string;
  /**
   * `thumbnailHeights[CoverSize.Small]` in the rail, `Medium` in the 3/5
   * column grids (the cast member's page, View all): the rails' Small is
   * upscaled x1.6 on a tablet.
   */
  thumbnailHeight: React.ComponentProps<typeof SeriesCover>["thumbnailHeight"];
}> = ({ show, origin, thumbnailHeight }) => (
  <div
    data-test-id={`recommendation-${show.id}`}
    className="flex flex-col gap-1 text-white"
  >
    <SeriesCover
      className="relative"
      seriesId={show.id}
      title={show.title}
      displayTitle={show.displayTitle}
      thumbnail={show.displayThumbnail.medium}
      thumbnailHeight={thumbnailHeight}
      views={show.views}
      episodeCount={show.episodesCount}
      origin={origin}
      detailsPage="always"
      size="sm"
    />
    <p className="line-clamp-2 text-xs font-semibold">{show.displayTitle}</p>
  </div>
);

const RecommendedShowCardSkeleton = () => (
  <div className="flex flex-col gap-1">
    <SeriesCover.Skeleton size="sm" />
    <Skeleton className="h-3.25 w-3/4" />
  </div>
);

export default Object.assign(RecommendedShowCard, {
  Skeleton: RecommendedShowCardSkeleton,
});
